import type { GridMode } from "@lancer-vtt/shared";
import { DEFAULT_GRID_MODE } from "@lancer-vtt/shared";
import type { EdgeMap, TerrainMap } from "../terrain";
import type { Annotation, AnnotationId } from "./annotations";
import type { AreaEffect, AreaEffectId } from "./areaEffects";
import type { Combatant, CombatantId } from "./combatant";
import type { Room, RoomId } from "./rooms";
import type { EncounterEnvironment, SitrepConfig, SitrepProgress } from "./sitrep";
import { DEFAULT_ENCOUNTER_GROUND, type EncounterGround } from "./ground";
import type { ActivationState } from "./turn";

/**
 * {@link EncounterState} itself — the whole encounter as one plain-JSON blob — plus the
 * accessors that read its optional fields.
 *
 * Every optional field here has a matching `encounter*` reader below. Read through the
 * reader, not the raw field: that is where an absent field's default lives, so a blob
 * written before the field existed loads cleanly instead of crashing a consumer.
 */

/**
 * The whole encounter: a roster of combatants plus the RAW activation state machine
 * (Core p.60), which is the sole turn surface and is **mandatory**.
 *
 * `combatants` is keyed by id, so a command names a combatant by id regardless of
 * position. `order` is a stable **display** order (an array of the same ids) and is never
 * indexed by a turn pointer — the activation machine derives "who acts next" from sides
 * plus the acted set, never `order[i]`. Keeping `combatants` (membership) separate from
 * `order` (display sequence) means reordering never touches combatant data.
 *
 * `schemaVersion` is the only persistence seam kept (mirroring `RosterState`): a
 * `vN → vN+1` plain-data migration pipeline.
 */
export type EncounterState = {
  schemaVersion: number;
  id: string;
  name: string;
  /** Combatants keyed by id (membership; id-addressed). */
  combatants: Record<CombatantId, Combatant>;
  /** Stable display order: combatant ids (not indexed by a turn pointer). */
  order: CombatantId[];
  /**
   * The encounter's **grid mode** — the dimensionality × cell shape of its map
   * (`2d-square`, `2d-hex`, `3d-hex`), chosen at creation and immutable. Read it through
   * {@link encounterGridMode} so a blob predating grid modes defaults to
   * {@link DEFAULT_GRID_MODE} rather than crashing.
   *
   * This is **live, not stored config**: `worldFor` routes a `2d-square` encounter to the
   * square (Chebyshev / rectangle-bound) world and the hex modes to the hex (disk-bound)
   * one.
   */
  gridMode?: GridMode;
  /**
   * The map's bound: tokens may only be placed/moved within a hex-disk of this radius
   * around the origin cell `(0,0)` — enough to make `move_token` server-authoritative
   * ("is this cell on the map?"). A square grid reads this as a Chebyshev disk. Tokens
   * whose footprint spills past it are rejected by `move_token`.
   */
  mapRadius: number;
  /**
   * The **2d-square grid's extent** in cells — `gridWidth` columns × `gridHeight` rows,
   * written by `resize_grid`. This is the square map's *own* extent, distinct from the
   * hex-disk {@link mapRadius}: the square renderer and the square coordinate system bound
   * against this rectangle, while `mapRadius` keeps bounding the hex-coordinate placement
   * system the hex modes use. Read both through {@link gridExtent}, which defaults each to
   * the square inscribing the hex disk (`2·mapRadius+1` per side).
   *
   * A dimension is an integer in `[{@link MIN_GRID_DIM}, {@link MAX_GRID_DIM}]` — the
   * reducer is the wall.
   */
  gridWidth?: number;
  gridHeight?: number;
  /**
   * The whole-cell terrain layer — a sparse map of cell key → typed flags
   * (solid/difficult/sight-blocking/cover). Default empty (a featureless field). The
   * reducer folds its movement-blocking and difficult-cost into the *same* `move_token`
   * validation seam token occupancy uses, and exposes its sight-blocking to LoS queries.
   * Plain JSON (a `Record`, not a `Map`) — the no-class invariant holds.
   */
  terrain: TerrainMap;
  /**
   * The per-edge obstacle layer — a sparse map of the **canonical shared-edge key** →
   * typed flags (movement/sight). A thin wall lives on the boundary *between* two adjacent
   * cells, where a whole-cell blocker can't go (units stand on both sides). Default empty.
   * Its movement-blocking folds into the *same* `enterCost(to, from)` seam token occupancy
   * and cell terrain use; its sight-blocking feeds edge-aware LoS. Plain JSON (a `Record`).
   */
  edges: EdgeMap;
  /**
   * The encounter's current **environment/hazard tag** — GM-set via `set_environment`,
   * read by the effects DSL's `scene_environment` predicate. Read it through
   * {@link encounterEnvironment}, which defaults an unset field to "no special
   * environment" (ordinary gravity, dry ground).
   */
  environment?: EncounterEnvironment;
  /**
   * The **map ground** the terrain art paints under every untiled cell — GM-set via `set_ground`
   * (`terrain-tile-art` ruling 8). Art only. Read it through {@link encounterGround}, which
   * defaults an unset field to forest floor.
   */
  ground?: EncounterGround;
  /**
   * The authored **rooms** layer — id-addressed {@link Room}s (a named union of rects).
   * Rooms carry **no mechanical meaning** (no LoS/movement/cover); the engine never reads
   * them, so this folds into no movement or sight seam. Default empty. Plain JSON (a
   * `Record`, not a `Map`). Read it through {@link encounterRooms}.
   */
  rooms?: Record<RoomId, Room>;
  /**
   * The authored **annotations** layer — id-addressed {@link Annotation}s (a click-vertex
   * polyline or a freehand stroke). Like rooms, purely visual: the engine never reads a
   * point's coords. Unlike rooms (GM-only authoring), this is the one primitive a player
   * may write to the map directly — gated in `canIssueEncounter` on
   * {@link Annotation.authorSlotId}, never UI-only. Default empty. Read it through
   * {@link encounterAnnotations}.
   */
  annotations?: Record<AnnotationId, Annotation>;
  /**
   * **Standing area effects on the map** — id-addressed {@link AreaEffect}s, one record
   * per cloud, materialized to cells on demand rather than stored per-cell.
   * `areaContributions.ts` folds every live entry into the SAME `softCell` closure
   * `terrainVisibility` builds from terrain and object contributions, so a unit walking
   * into or out of a cloud costs zero area-specific code: cover is a live positional
   * query, never a stamped status. Created and removed by the `area_add`/`area_remove`
   * resolved steps — the structural mirror of `spawn_object`/`despawn_object` — never a
   * standalone top-level command. Default empty.
   */
  areaEffects?: Record<AreaEffectId, AreaEffect>;
  /**
   * The **RAW activation state machine** (Core p.60) — the sole turn surface. It owns the
   * round counter, whose side acts next, the acted set, the per-round reaction ledger, and
   * the discriminated {@link Turn} (`idle` vs `acting`-with-budget). **Hard-enforced:** the
   * play transitions `activate`/`endTurn`/`endRound` reject every illegal move; cross-side
   * fiat lives only in the `set_activation` override, never in a play command.
   *
   * **Two-phase turns:** `activate(X)` (legal only when the turn is `idle` and X is alive,
   * un-acted, and on `activeSide`) sets `turn = { phase: "acting", actor: X, budget }`,
   * refilling X's per-turn action/move budget; `endTurn(X)` (legal only when the acting
   * actor is X) moves X into `activated`, returns the turn to `idle`, and flips
   * `activeSide` — unevenly, staying on the side that still has able un-acted units.
   * `endRound` (legal only when the turn is `idle` and the round is complete) clears
   * `activated`, refills reactions, bumps `round`, and opens the new round on the side that
   * did *not* act last.
   *
   * **Derived, not stored, "order":** the un-acted pool is derived on demand from
   * `combatants`/`order` minus `activated` minus the acting actor, filtered by side (see
   * {@link unactedOnSide}); sides pick dynamically. A unit's side is its frozen
   * `base.allegiance` ({@link sideOf}) — a spawn-time classifier, not a live field.
   *
   * **Mandatory:** every spawned/migrated encounter carries one. There is no optional
   * pre-activation tier.
   */
  activation: ActivationState;
  /**
   * **Per-encounter live-HP policy** — whether a `disclosure === "full"` hostile's
   * **current** HP streams live to player clients. Gates ONLY the transient current-HP
   * number/bar; the frozen base stats (max HP, SPD/EVA/ARM/skills, weapons) that `"full"`
   * reveals are unaffected, since those don't drift in combat and revealing them is already
   * snapshot-faithful.
   *
   * **Encounter-level, not per-combatant, and NOT a client preference**: a live-HP policy
   * is a *projection input* (it changes what the server broadcasts), so it must be
   * authoritative synced state, set by the GM-only undoable `set_hp_disclosure` — never a
   * per-GM client pref like the auto-wall toggle. It lives on `EncounterState` rather than
   * `GameState` because `projectEncounterFor` already receives the `EncounterState`.
   *
   * Read through {@link encounterShowEnemyHp}, which defaults to `false` (RAW-faithful — no
   * live HP feed).
   */
  showEnemyHp?: boolean;
  /**
   * The **authored sitrep** (Core pp. 267–273) — the whole victory-condition config
   * (`type`, zones, round limit, optional objective). Set and cleared in bulk by the
   * GM-only `set_sitrep`; never partially edited by any other command.
   *
   * **Absent means deathmatch** — the RAW default of "no printed sitrep". The pure victory
   * read (`sitrepStatus`) treats an unset `sitrep` as the deathmatch branch: one allegiance
   * eliminated ⇒ decided.
   */
  sitrep?: SitrepConfig;
  /**
   * **Control's per-round accumulated score** — the one piece of path-dependent sitrep
   * state (see {@link SitrepProgress}). Seeded by `set_sitrep` iff
   * `sitrep.type === "control"`; absent for every other sitrep type and whenever `sitrep`
   * itself is unset. Cleared alongside `sitrep` by a `set_sitrep` clear payload.
   */
  sitrepProgress?: SitrepProgress;
  /**
   * **The fight is over** — the ISO timestamp the GM's `end_combat` stamped, absent while the
   * fight is live. Ending keeps the map and every combatant exactly as they stood (the scene stays
   * up for the wrap-up, `decisions/0203` point 2); what it changes is which commands the reducer
   * accepts: the turn, activation and action-economy family is refused
   * (`COMBAT_PLAY_COMMANDS`, `../combatOver.ts`) and the room refuses every resolution intent,
   * while GM edits (HP, statuses, token moves, terrain, notes) stay open. The GM's
   * `reopen_combat` (also the end's inverse) clears it, so reopening or undoing the end resumes the
   * fight where it stopped. Read it
   * through {@link isCombatOver}.
   */
  combatEndedAt?: string;
}

/** An encounter's grid mode, defaulting a pre-grid-mode blob to {@link DEFAULT_GRID_MODE}. */
export function encounterGridMode(state: Pick<EncounterState, "gridMode">): GridMode {
  return state.gridMode ?? DEFAULT_GRID_MODE;
}

/** An encounter's authored rooms, defaulting a blob predating rooms to an empty map. */
export function encounterRooms(state: Pick<EncounterState, "rooms">): Record<RoomId, Room> {
  return state.rooms ?? {};
}

/**
 * An encounter's current environment/hazard tag, defaulting an unset blob to `null` ("no
 * special environment"). `null`, not `undefined`, so a consumer's `=== null` check reads as
 * an explicit "ordinary" fact rather than "field not read yet."
 */
export function encounterEnvironment(
  state: Pick<EncounterState, "environment">,
): EncounterEnvironment | null {
  return state.environment ?? null;
}

/** An encounter's map ground, defaulting an unset blob to {@link DEFAULT_ENCOUNTER_GROUND}. */
export function encounterGround(state: Pick<EncounterState, "ground">): EncounterGround {
  return state.ground ?? DEFAULT_ENCOUNTER_GROUND;
}

/** An encounter's authored annotations, defaulting a blob predating annotations to an empty map. */
export function encounterAnnotations(state: Pick<EncounterState, "annotations">): Record<AnnotationId, Annotation> {
  return state.annotations ?? {};
}

/** An encounter's live-HP policy, defaulting a blob predating it (or never toggled) to `false` (RAW-faithful). */
export function encounterShowEnemyHp(state: Pick<EncounterState, "showEnemyHp">): boolean {
  return state.showEnemyHp ?? false;
}

/** Whether the GM has ended this fight (`end_combat`); a blob predating the field reads as live. */
export function isCombatOver(state: Pick<EncounterState, "combatEndedAt">): boolean {
  return state.combatEndedAt !== undefined;
}

/** Min/max for a square-grid dimension — a `resize_grid` width/height is an integer in `[1, 64]`. */
export const MIN_GRID_DIM = 1;
export const MAX_GRID_DIM = 64;

/** A square grid's extent in cells: `width` columns × `height` rows. */
export type GridExtent = { width: number; height: number };

/**
 * The encounter's 2d-square grid extent (columns × rows), defaulting a blob that predates
 * explicit dimensions to the square that inscribes its hex-disk `mapRadius` (`2·radius+1`
 * per side) — so an encounter created before `resize_grid` renders exactly as it did. The
 * square renderer and the square coordinate system read their bound here.
 */
export function gridExtent(
  state: Pick<EncounterState, "gridWidth" | "gridHeight" | "mapRadius">,
): GridExtent {
  const fallback = 2 * Math.max(0, state.mapRadius) + 1;
  return {
    width: state.gridWidth ?? fallback,
    height: state.gridHeight ?? fallback,
  };
}
